import React, { FC, useState, useEffect, useMemo } from 'react';
import AuthenticationContext, { Session } from 'utils/auth/context';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { local } from 'utils/storage';

export const SESSION_KEY = 'session';

export const MockAuth0Provider: FC<{
  children: React.ReactNode;
}> = ({ children }): JSX.Element => {
  const [session, setSession] = useState<Session>();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const logout = async () => {
    setIsAuthenticated(false);
    setSession(undefined);
    local.removeItem(SESSION_KEY);
    navigate('/sign-in');
  };

  const loginWithRedirect = async () => {
    navigate('/sign-in');
  };

  const restoreSession = () => {
    let restoredSession: Session | undefined;
    const json = local.getItem(SESSION_KEY);
    if (json) {
      try {
        restoredSession = JSON.parse(json) as Session;
      } catch {
        throw new Error(t('error.description'));
      }
    }
    setSession(restoredSession);
    return restoredSession;
  };

  const getAccessTokenSilently = () =>
    Promise.resolve(session?.accessToken ?? '');

  const getIdTokenClaims = () =>
    Promise.resolve({
      email_verified: session?.emailVerified,
      __raw: '',
      'https://myid.resideo.com/signUpUserType': session?.signUpUserType,
    });

  useEffect(() => {
    const s = restoreSession();
    setIsAuthenticated(!!s);
    setIsLoading(false);
  }, []);

  const value = useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      restoreSession,
      setIsAuthenticated,
      loginWithRedirect,
      logout,
      getAccessTokenSilently,
      getIdTokenClaims,
    }),
    [
      isLoading,
      isAuthenticated,
      restoreSession,
      setIsAuthenticated,
      loginWithRedirect,
      logout,
      getAccessTokenSilently,
      getIdTokenClaims,
    ]
  );

  return (
    <AuthenticationContext.Provider value={value as any}>
      {children}
    </AuthenticationContext.Provider>
  );
};
